import { handleActions, combineActions } from 'redux-actions'
import _ from 'lodash'

import { isPdfPage } from '@tabeeb/services/pageService'
import { getReadonlyContentSuccess } from '@tabeeb/shared/content/actions'
import { convertReadonlyPageToPluginPage } from '@tabeeb/services/dataConverter'

import * as rawActions from '../actions'
import { AnnotationType } from '../../../Enums'

const defaultState = []

function delPage(state, { pageId }) {
  const deleteItem = state.find((page) => page.id === pageId)
  if (!deleteItem) {
    return state
  }

  let newState

  if (isPdfPage(deleteItem.contentType)) {
    newState = state.filter((page) => page.contentUrl !== deleteItem.contentUrl)
  } else {
    newState = state.filter((page) => page.id !== deleteItem.id)
  }

  for (const page of newState) {
    if (page.annotations) {
      const containsAnnotationsLinkedToDeletedPage = page.annotations.some(
        (annotation) => annotation.Type === AnnotationType.LinkedPage && annotation.LinkedPageId === pageId
      )
      if (containsAnnotationsLinkedToDeletedPage) {
        page.annotations = page.annotations.filter(
          (annotation) => !(annotation.Type === AnnotationType.LinkedPage && annotation.LinkedPageId === pageId)
        )
      }
    }
  }

  return newState
}

function _addPage(state, page) {
  const resultPages = [...state]
  const index = resultPages.findIndex((oldPage) => oldPage.id === page.id)

  if (index === -1) {
    resultPages.push(page)
  } else {
    resultPages[index] = { ...resultPages[index], ...page }
  }

  return resultPages
}

function renamePage(state, { pageId, name }) {
  return state.map((page) =>
    page.id === pageId
      ? {
          ...page,
          name,
          thumbnailCaption: name,
        }
      : page
  )
}

function setPageAsLoaded(state, { pageId }) {
  return state.map((page) => (page.id === pageId ? { ...page, isLoaded: true } : page))
}

function setThumbnailIsLoaded(state, { pageId }) {
  return state.map((page) => (page.id === pageId ? { ...page, isThumbnailLoaded: true } : page))
}

function updatePageThumbnail(state, { pageId, thumbnailUrl = null }) {
  const resultPages = [...state]
  const index = resultPages.findIndex((oldPage) => oldPage.id === pageId)

  if (index === -1) {
    return resultPages
  }

  resultPages[index] = { ...resultPages[index], timestampReceived: Date.now() }
  if (thumbnailUrl) {
    resultPages[index].thumbnailUrl = thumbnailUrl
  }

  return resultPages
}

function _addAnnotations(state, annotations) {
  const pageId = annotations[0].PageId

  return state.map((page) => {
    if (page.id !== pageId) {
      return page
    }

    return {
      ...page,
      annotations: _.uniqBy([...annotations, ...page.annotations], (annotation) => annotation.Id),
    }
  })
}

function _addAnnotation(state, annotation) {
  return _addAnnotations(state, [annotation])
}

function _delAnnotations(state, annotations) {
  const pageId = annotations[0]?.PageId
  if (!pageId) {
    return state
  }

  const ids = annotations.map((annotation) => annotation.Id)

  return state.map((page) => {
    if (page.id !== pageId) {
      return page
    }

    return {
      ...page,
      annotations: page.annotations.filter((annotation) => !ids.includes(annotation.Id)),
    }
  })
}

function updateOrAddAsset(state, updatedAsset) {
  const assetId = updatedAsset.Id
  const pageId = updatedAsset.PageId

  const assetPage = state.find((element) => element.id === pageId)
  if (!assetPage) {
    return state
  }

  const existingAsset = assetPage.assets.find((element) => element.Id === assetId)
  if (existingAsset) {
    return state.map((page) =>
      page.id === pageId
        ? {
            ...page,
            assets: page.assets.map((asset) => (asset.Id === assetId ? updatedAsset : asset)),
          }
        : page
    )
  }

  return state.map((page) => (page.id === pageId ? { ...page, assets: [...page.assets, updatedAsset] } : page))
}

export default handleActions(
  {
    [combineActions(rawActions.setGalleryItems, rawActions.addGalleryItems)](state, action) {
      const pages = _.sortBy(action.payload, ['order', 'pdfURL', 'pdfPageNumber', 'timestampCreated', 'id'])
      return pages
    },
    [getReadonlyContentSuccess](state, { payload: { Pages } }) {
      const convertedPages = Pages.map((page) => convertReadonlyPageToPluginPage(page))
      const pages = _.sortBy(convertedPages, ['order', 'pdfURL', 'pdfPageNumber', 'timestampCreated', 'id'])
      return pages
    },
    [combineActions(rawActions.addGalleryItem, rawActions.updateGalleryItem)](state, action) {
      return _.sortBy(_addPage(state, action.payload), [
        'order',
        'pdfPageIndex',
        'pdfPageNumber',
        'timestampCreated',
        'id',
      ])
    },
    [combineActions(rawActions.deleteGalleryItem)](state, action) {
      return delPage(state, action.payload)
    },
    [combineActions(rawActions.renameGalleryItemSuccess, rawActions.updateGalleryItemName)](state, action) {
      return renamePage(state, action.payload)
    },
    [rawActions.setPageAsLoaded](state, action) {
      return setPageAsLoaded(state, action.payload)
    },
    [rawActions.setGalleryItemThumbnailIsLoaded](state, action) {
      return setThumbnailIsLoaded(state, action.payload)
    },
    [rawActions.updateGalleryItemThumbnail](state, action) {
      return updatePageThumbnail(state, action.payload)
    },
    [rawActions.addAnnotation](state, action) {
      return _addAnnotation(state, action.payload)
    },
    [rawActions.addAnnotations](state, action) {
      return _addAnnotations(state, action.payload)
    },
    [rawActions.updateAnnotation](state, action) {
      return _addAnnotation(state, action.payload)
    },
    [rawActions.deleteAnnotations](state, action) {
      return _delAnnotations(state, action.payload)
    },
    [rawActions.updateOrAddAsset](state, action) {
      return updateOrAddAsset(state, action.payload)
    },
    [rawActions.resetGalleryState](state, action) {
      return defaultState
    },
  },
  defaultState
)
